import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';

@Component({
    selector: 'app-cron-editor',
    templateUrl: './cron-editor.component.html'
})
export class CronEditorComponent implements OnInit {
    @Input() cronExpression: string = '0 0 * * * *';
    @Output() cronExpressionChange = new EventEmitter<string>();

    seconds: string = '0';
    minutes: string = '0';
    hours: string = '*';
    dayOfMonth: string = '*';
    month: string = '*';
    dayOfWeek: string = '*';

    selectedTab: string = 'minutes';

    minutesOptions = [
        { label: 'Chaque minute', value: '*' },
        { label: 'Toutes les 5 minutes', value: '*/5' },
        { label: 'Toutes les 10 minutes', value: '*/10' },
        { label: 'Toutes les 15 minutes', value: '*/15' },
        { label: 'Toutes les 30 minutes', value: '*/30' },
        { label: 'Spécifique', value: 'custom' }
    ];

    hoursOptions = [
        { label: 'Chaque heure', value: '*' },
        { label: 'Toutes les 2 heures', value: '*/2' },
        { label: 'Toutes les 3 heures', value: '*/3' },
        { label: 'Toutes les 4 heures', value: '*/4' },
        { label: 'Toutes les 6 heures', value: '*/6' },
        { label: 'Toutes les 12 heures', value: '*/12' },
        { label: 'Spécifique', value: 'custom' }
    ];

    daysOptions = [
        { label: 'Chaque jour', value: '*' },
        { label: 'Jour spécifique du mois', value: 'dom' },
        { label: 'Jour spécifique de la semaine', value: 'dow' }
    ];

    selectedMinutesOption: string = '*';
    selectedHoursOption: string = '*';
    selectedDaysOption: string = '*';
    specificMinute: number = 0;
    specificHour: number = 0;
    specificDayOfMonth: number = 1;
    specificMonth: string = '*';
    specificDayOfWeek: number = 0;

    weekdays = [
        { label: 'Dimanche', value: '0' },
        { label: 'Lundi', value: '1' },
        { label: 'Mardi', value: '2' },
        { label: 'Mercredi', value: '3' },
        { label: 'Jeudi', value: '4' },
        { label: 'Vendredi', value: '5' },
        { label: 'Samedi', value: '6' }
    ];

    months = [
        { label: 'Tous les mois', value: '*' },
        { label: 'Janvier', value: '1' },
        { label: 'Février', value: '2' },
        { label: 'Mars', value: '3' },
        { label: 'Avril', value: '4' },
        { label: 'Mai', value: '5' },
        { label: 'Juin', value: '6' },
        { label: 'Juillet', value: '7' },
        { label: 'Août', value: '8' },
        { label: 'Septembre', value: '9' },
        { label: 'Octobre', value: '10' },
        { label: 'Novembre', value: '11' },
        { label: 'Décembre', value: '12' }
    ];

    ngOnInit(): void {
        this.parseCronExpression();
    }

    parseCronExpression(): void {
        const parts = this.cronExpression.split(' ');
        if (parts.length === 6) {
            this.seconds = parts[0];
            this.minutes = parts[1];
            this.hours = parts[2];
            this.dayOfMonth = parts[3];
            this.month = parts[4];
            this.dayOfWeek = parts[5];

            // Set appropriate selected options based on parsed values
            this.selectedMinutesOption = this.getMatchingOption(this.minutes, this.minutesOptions) || 'custom';
            this.selectedHoursOption = this.getMatchingOption(this.hours, this.hoursOptions) || 'custom';
            
            // Determine which day option is selected
            if (this.dayOfMonth !== '*' && this.dayOfMonth !== '?' && (this.dayOfWeek === '*' || this.dayOfWeek === '?')) {
                this.selectedDaysOption = 'dom';
            } else if ((this.dayOfMonth === '*' || this.dayOfMonth === '?') && this.dayOfWeek !== '*' && this.dayOfWeek !== '?') {
                this.selectedDaysOption = 'dow';
            } else {
                this.selectedDaysOption = '*';
            }

            // Set specific values
            if (this.selectedMinutesOption === 'custom' && !isNaN(parseInt(this.minutes))) {
                this.specificMinute = parseInt(this.minutes);
            }
            if (this.selectedHoursOption === 'custom' && !isNaN(parseInt(this.hours))) {
                this.specificHour = parseInt(this.hours);
            }
            if (this.selectedDaysOption === 'dom' && !isNaN(parseInt(this.dayOfMonth))) {
                this.specificDayOfMonth = parseInt(this.dayOfMonth);
            }
            
            // Update to handle * for month
            this.specificMonth = this.month;
            
            if (this.selectedDaysOption === 'dow' && !isNaN(parseInt(this.dayOfWeek))) {
                this.specificDayOfWeek = parseInt(this.dayOfWeek);
            }
        }
    }

    getMatchingOption(value: string, options: any[]): string | null {
        for (const option of options) {
            if (option.value === value) {
                return option.value;
            }
        }
        return null;
    }

    updateCronExpression(): void {
        // Update minutes
        if (this.selectedMinutesOption === 'custom') {
            this.minutes = this.specificMinute.toString();
        } else {
            this.minutes = this.selectedMinutesOption;
        }

        // Update hours
        if (this.selectedHoursOption === 'custom') {
            this.hours = this.specificHour.toString();
        } else {
            this.hours = this.selectedHoursOption;
        }

        // Update days - enforce the constraint that either day-of-month or day-of-week must be ?
        if (this.selectedDaysOption === '*') {
            this.dayOfMonth = '*';
            this.dayOfWeek = '?'; // Use question mark for day of week when not specified
        } else if (this.selectedDaysOption === 'dom') {
            this.dayOfMonth = this.specificDayOfMonth.toString();
            this.dayOfWeek = '?'; // Use question mark for day of week when day of month is specified
        } else if (this.selectedDaysOption === 'dow') {
            this.dayOfMonth = '?'; // Use question mark for day of month when day of week is specified
            this.dayOfWeek = this.specificDayOfWeek.toString();
        }

        // Set month value (can be * or a specific month number)
        this.month = this.specificMonth;

        // Construct cron expression
        const cronExpression = `${this.seconds} ${this.minutes} ${this.hours} ${this.dayOfMonth} ${this.month} ${this.dayOfWeek}`;
        this.cronExpression = cronExpression;
        this.cronExpressionChange.emit(cronExpression);
    }

    onMinutesChange(): void {
        this.updateCronExpression();
    }

    onHoursChange(): void {
        this.updateCronExpression();
    }

    onDaysChange(): void {
        this.updateCronExpression();
    }

    onSpecificValueChange(): void {
        this.updateCronExpression();
    }

    selectTab(tab: string): void {
        this.selectedTab = tab;
    }

    usePreset(preset: string): void {
        // For presets, ensure the day constraint is satisfied
        const parts = preset.split(' ');
        if (parts.length === 6) {
            const dayOfMonth = parts[3];
            const dayOfWeek = parts[5];
            
            // If both are specified, prioritize day of month and set day of week to ?
            if (dayOfMonth !== '*' && dayOfWeek !== '*') {
                parts[5] = '?';
            } 
            // If dayOfMonth is *, ensure dayOfWeek has a value (not *)
            else if (dayOfMonth === '*' && dayOfWeek === '*') {
                parts[5] = '?';
            }
            // If dayOfWeek is specified, ensure dayOfMonth is ?
            else if (dayOfWeek !== '*' && dayOfWeek !== '?') {
                parts[3] = '?';
            }
            // If dayOfMonth is specified, ensure dayOfWeek is ?
            else if (dayOfMonth !== '*' && dayOfMonth !== '?') {
                parts[5] = '?';
            }
            
            preset = parts.join(' ');
        }
        
        this.cronExpression = preset;
        this.parseCronExpression();
        this.cronExpressionChange.emit(preset);
    }
} 